import React, { useState } from "react";
import {
  Alert,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

import PasswordInput from "../../../Authentication/Components/FormControl/Components/PasswordInput";
import PasswordWithTooltip from "../../../Authentication/Components/FormControl/Components/PasswordWithTooltip";

import { ApiError } from "@/schemas/api/error";
import { type AppUserSession } from "@/schemas/auth/session";
import { isValidPassword } from "@/schemas/forms/password";
import AuthService from "@/services/internal/auth";
import { alertUnexpectedApiError } from "@/utils/alerts";

type UpdatePasswordFormProps = {
  session: AppUserSession;
};

function UpdatePasswordForm({
  session,
}: UpdatePasswordFormProps): React.JSX.Element {
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const validateInputs = (): boolean => {
    const passwordValidation = isValidPassword(newPassword);
    if (!passwordValidation.isValid) {
      Alert.alert("Invalid Password", passwordValidation.message, [
        { text: "OK" },
      ]);
      return false;
    }

    if (newPassword !== confirmPassword) {
      Alert.alert(
        "Password Mismatch",
        "The new password does not match the confirmation password. Please try again.",
        [{ text: "OK" }],
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (): Promise<void> => {
    if (validateInputs()) {
      try {
        const authService = new AuthService();
        await authService.changePassword({
          accessToken: session.token.accessToken,
          currentPassword,
          newPassword,
        });
        Alert.alert(
          "Password Updated",
          "Your password has been updated successfully.",
          [{ text: "OK" }],
        );
      } catch (error) {
        const apiError = error as ApiError;
        alertUnexpectedApiError(apiError);
      }
    }
  };

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.formContainer}
      >
        <View style={styles.card}>
          <Text style={styles.title}>Change Password</Text>
          <Text style={styles.label}>Current Password</Text>
          <PasswordInput
            value={currentPassword}
            onChangeText={setCurrentPassword}
            placeholder="Enter current password"
          />

          <PasswordWithTooltip />
          <PasswordInput
            value={newPassword}
            onChangeText={setNewPassword}
            placeholder="Enter new password"
          />

          <Text style={styles.label}>Confirm Password</Text>
          <PasswordInput
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            placeholder="Confirm new password"
          />

          <TouchableOpacity style={styles.submitButton} onPress={handleSubmit}>
            <Text style={styles.submitButtonText}>Change Password</Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
  },
  formContainer: {
    width: "100%",
    maxWidth: 400,
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: 10,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 30,
    textAlign: "center",
    color: "#4A5568",
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
    color: "#4A5568",
  },
  submitButton: {
    backgroundColor: "#4299E1",
    borderRadius: 5,
    padding: 15,
    alignItems: "center",
    marginTop: 10,
  },
  submitButtonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "600",
  },
});

export default UpdatePasswordForm;
